import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type RefObject,
} from 'react';
import type { DraggableData } from 'react-draggable';

export type FabPosition = { x: number; y: number };
type FabBounds = { top: number; left: number; right: number; bottom: number };

// FAB is 48px, CSS-anchored bottom-right (`.fabWrapper` inset 1.5rem on both edges).
const FAB_SIZE = 48;
const FAB_HEADER_CLEARANCE = 72; // keep clear of the ~64px MainHeader + gap
const FAB_EDGE_MARGIN = 24; // 1.5rem `.fabWrapper` anchor inset (bottom AND right)
const FAB_DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag

// react-draggable's translate is relative to the CSS-anchored corner, so each
// travel limit must subtract the anchor inset or the FAB overshoots the viewport.
// Vertical: at offset 0 the FAB top sits at innerHeight - FAB_EDGE_MARGIN - FAB_SIZE;
// the most-negative y that keeps it at/below FAB_HEADER_CLEARANCE is therefore
// -(innerHeight - FAB_SIZE - FAB_HEADER_CLEARANCE - FAB_EDGE_MARGIN).
export function computeFabBounds(): FabBounds | undefined {
  if (typeof window === 'undefined') return undefined;
  return {
    top: -(
      window.innerHeight - FAB_SIZE - FAB_HEADER_CLEARANCE - FAB_EDGE_MARGIN
    ),
    left: -(window.innerWidth - FAB_SIZE - FAB_EDGE_MARGIN),
    right: 0,
    bottom: 0,
  };
}

/** Clamp a saved offset into the current bounds so a position saved on a larger
 *  viewport can't restore off-screen on a smaller one. */
export function clampFabToBounds(
  pos: FabPosition,
  bounds: FabBounds | undefined
): FabPosition {
  if (!bounds) return pos;
  return {
    x: Math.min(bounds.right, Math.max(bounds.left, pos.x)),
    y: Math.min(bounds.bottom, Math.max(bounds.top, pos.y)),
  };
}

interface UseFabDraggableOptions {
  /** Persisted drag offset from the store (null = default corner). */
  readonly persisted: FabPosition | null;
  /** Persist a new offset (e.g. dispatch a store action). Stability not required. */
  readonly onPersist: (pos: FabPosition) => void;
}

interface UseFabDraggableResult {
  readonly position: FabPosition;
  readonly bounds: FabBounds | undefined;
  readonly nodeRef: RefObject<HTMLDivElement>;
  readonly draggableHandlers: {
    readonly onStart: (e: unknown, data: DraggableData) => void;
    readonly onDrag: (e: unknown, data: DraggableData) => void;
    readonly onStop: (e: unknown, data: DraggableData) => void;
  };
  /** True immediately after a real (past-threshold) drag; the caller's onClick
   *  reads + resets it to swallow the trailing synthetic click. */
  readonly draggedRef: MutableRefObject<boolean>;
}

/**
 * Encapsulates the draggable-FAB behavior (controlled react-draggable position,
 * viewport bounds + resize re-clamp, drag-vs-click threshold, and persistence
 * wiring) so the host component stays thin. FAB-specific by design — not a generic
 * utility.
 */
export function useFabDraggable({
  persisted,
  onPersist,
}: UseFabDraggableOptions): UseFabDraggableResult {
  // Ref-stabilize onPersist so consumers needn't memoize it and our effects/
  // handlers don't re-subscribe when an inline callback identity changes.
  // Written during render (not via an effect) so it's always current — the resize
  // listener and onStop read it on a user gesture, never mid-render.
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;

  const [bounds, setBounds] = useState(computeFabBounds);
  const [position, setPosition] = useState(() =>
    clampFabToBounds(persisted ?? { x: 0, y: 0 }, computeFabBounds())
  );

  const nodeRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<FabPosition>({ x: 0, y: 0 });
  const draggedRef = useRef(false);
  // Mirror of position written SYNCHRONOUSLY (not via a lagging effect) so the
  // resize handler's stable closure never reads a stale offset mid-drag.
  const positionRef = useRef(position);
  const applyPosition = useCallback((pos: FabPosition) => {
    positionRef.current = pos;
    setPosition(pos);
  }, []);

  // Re-sync the controlled position when the persisted value changes from outside
  // (notably REHYDRATE_FROM_PERSONA swapping persona-scoped uiSettings after mount).
  useEffect(() => {
    applyPosition(
      clampFabToBounds(persisted ?? { x: 0, y: 0 }, computeFabBounds())
    );
  }, [persisted, applyPosition]);

  // On resize: refresh bounds AND re-clamp the live + persisted position.
  useEffect(() => {
    const onResize = () => {
      const next = computeFabBounds();
      setBounds(next);
      const cur = positionRef.current;
      const clamped = clampFabToBounds(cur, next);
      applyPosition(clamped);
      if (clamped.x !== cur.x || clamped.y !== cur.y) {
        onPersistRef.current(clamped);
      }
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, [applyPosition]);

  const onStart = useCallback((_e: unknown, data: DraggableData) => {
    dragStartRef.current = { x: data.x, y: data.y };
    draggedRef.current = false;
  }, []);

  const onDrag = useCallback(
    (_e: unknown, data: DraggableData) => {
      applyPosition({ x: data.x, y: data.y });
    },
    [applyPosition]
  );

  const onStop = useCallback(
    (_e: unknown, data: DraggableData) => {
      const start = dragStartRef.current;
      const moved = Math.hypot(data.x - start.x, data.y - start.y);
      if (moved < FAB_DRAG_THRESHOLD) {
        // A slip while tapping, not a drag: snap back; let the click open chat.
        applyPosition(start);
        return;
      }
      draggedRef.current = true; // swallow the trailing synthetic click
      const next = { x: data.x, y: data.y };
      applyPosition(next);
      onPersistRef.current(next);
    },
    [applyPosition]
  );

  return {
    position,
    bounds,
    nodeRef,
    draggableHandlers: { onStart, onDrag, onStop },
    draggedRef,
  };
}
